import React, { useState, useEffect } from 'react';
import { Search, ShoppingCart, Heart, Star, Play, TrendingUp, CheckCircle, Filter, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Marketplace = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMood, setSelectedMood] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cartItems, setCartItems] = useState([]);
  const navigate = useNavigate();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Mood filters - professional, no emojis
  const moodFilters = [
    { id: 'all', label: 'All' },
    { id: 'peaceful', label: 'Peaceful' },
    { id: 'calm', label: 'Calm' },
    { id: 'uplifting', label: 'Uplifting' },
    { id: 'reflective', label: 'Reflective' },
    { id: 'motivated', label: 'Motivated' },
    { id: 'grounding', label: 'Grounding' },
    { id: 'inspiring', label: 'Inspiring' },
    { id: 'energizing', label: 'Energizing' }
  ];

  const categories = [
    { id: 'all', label: 'All Categories' },
    { id: 'audio', label: 'Audio', type: 'product_type' },
    { id: 'video', label: 'Videos', type: 'product_type' },
    { id: 'course', label: 'Courses', type: 'product_type' },
    { id: 'document', label: 'Ebooks', type: 'product_type' },
    { id: 'Mindfulness & Meditation', label: 'Mindfulness & Meditation', type: 'category' },
    { id: 'Business & Leadership', label: 'Business & Leadership', type: 'category' },
    { id: 'Books & Guides', label: 'Books & Guides', type: 'category' }
  ];

  useEffect(() => {
    fetchProducts();
    // Load cart from localStorage
    const savedCart = localStorage.getItem('marketplace_cart');
    if (savedCart) {
      setCartItems(JSON.parse(savedCart));
    }
  }, []);

  useEffect(() => {
    filterProducts();
  }, [selectedMood, selectedCategory, searchQuery, products]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/marketplace/browse`);
      const data = await response.json();
      console.log('Fetched products:', data);
      setProducts(data);
      setFilteredProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = [...products];

    // Filter by mood
    if (selectedMood !== 'all') {
      filtered = filtered.filter(p => p.mood_tags && p.mood_tags.includes(selectedMood));
    }

    // Filter by category or product type
    if (selectedCategory !== 'all') {
      const categoryConfig = categories.find(c => c.id === selectedCategory);
      if (categoryConfig) {
        if (categoryConfig.type === 'product_type') {
          filtered = filtered.filter(p => p.product_type === selectedCategory);
        } else if (categoryConfig.type === 'category') {
          filtered = filtered.filter(p => p.category === selectedCategory);
        }
      }
    }

    // Filter by search
    if (searchQuery) {
      filtered = filtered.filter(p => 
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    console.log('Filtered products:', filtered.length, 'from', products.length);
    setFilteredProducts(filtered);
  };

  const addToCart = (product, e) => {
    e.stopPropagation();
    const newCart = [...cartItems, product];
    setCartItems(newCart);
    localStorage.setItem('marketplace_cart', JSON.stringify(newCart));
    alert(`"${product.title}" added to cart!`);
  };

  const goToCart = () => {
    navigate('/marketplace/cart');
  };

  const goToSellerOnboarding = () => {
    navigate('/marketplace/become-seller');
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading marketplace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Conscious Commerce</h1>
              <p className="text-sm text-gray-600 mt-1">Transformational digital content from certified conscious organizations</p>
            </div>
            <div className="flex items-center space-x-4">
              <button className="flex items-center space-x-2 px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors">
                <Heart className="w-5 h-5" />
                <span className="text-sm font-medium">Wishlist</span>
              </button>
              <button 
                onClick={goToCart}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors relative"
              >
                <ShoppingCart className="w-5 h-5" />
                <span className="text-sm font-medium">Cart</span>
                {cartItems.length > 0 && (
                  <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {cartItems.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search for courses, music, podcasts, and more..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Hero Mood Section */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Explore by Mood</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              { mood: 'happy', label: 'Happy', gradient: 'from-yellow-400 to-orange-400', image: 'https://images.unsplash.com/photo-1502139214982-d0ad755818d8?w=400' },
              { mood: 'motivated', label: 'Motivated', gradient: 'from-red-400 to-pink-500', image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400' },
              { mood: 'creative', label: 'Creative', gradient: 'from-purple-400 to-indigo-500', image: 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400' },
              { mood: 'spiritual', label: 'Spiritual', gradient: 'from-indigo-400 to-purple-500', image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400' },
              { mood: 'energizing', label: 'Energizing', gradient: 'from-green-400 to-teal-500', image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400' },
              { mood: 'peaceful', label: 'Peaceful', gradient: 'from-blue-400 to-cyan-400', image: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=400' }
            ].map((item) => (
              <button
                key={item.mood}
                onClick={() => setSelectedMood(item.mood)}
                className={`relative h-32 rounded-xl overflow-hidden group cursor-pointer transition-transform hover:scale-105 ${
                  selectedMood === item.mood ? 'ring-4 ring-purple-600' : ''
                }`}
              >
                <img 
                  src={item.image} 
                  alt={item.label}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-60 group-hover:opacity-50 transition-opacity`}></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white font-bold text-lg">{item.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="flex items-center space-x-4 mb-6 overflow-x-auto pb-2">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Filter className="w-4 h-4" />
            <span className="font-medium">Filter:</span>
          </div>
          {moodFilters.map(mood => (
            <button
              key={mood.id}
              onClick={() => setSelectedMood(mood.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                selectedMood === mood.id
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-purple-300'
              }`}
            >
              {mood.label}
              {selectedMood === mood.id && mood.id !== 'all' && (
                <span className="ml-2 text-xs">✕</span>
              )}
            </button>
          ))}
        </div>

        {/* Category Tabs */}
        <div className="flex items-center space-x-2 mb-8 overflow-x-auto pb-2 border-b border-gray-200">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-all border-b-2 ${
                selectedCategory === cat.id
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            <span className="font-semibold text-gray-900">{filteredProducts.length}</span> products found
          </p>
        </div>

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map(product => (
              <div
                key={product.product_id}
                onClick={() => navigate(`/marketplace/product/${product.product_id}`)}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-xl hover:border-purple-300 transition-all duration-300 cursor-pointer group"
              >
                {/* Product Image */}
                <div className="relative h-48 bg-gradient-to-br from-purple-100 to-pink-100 overflow-hidden">
                  {(product.product_type === 'video' || product.product_type === 'course') && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Play className="w-12 h-12 text-white opacity-80 group-hover:scale-110 transition-transform" />
                    </div>
                  )}
                  <div className="absolute top-2 right-2">
                    <button className="p-2 bg-white rounded-full shadow-md hover:bg-pink-50 transition-colors">
                      <Heart className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                  {product.is_featured && (
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full">Featured</span>
                    </div>
                  )}
                </div>

                {/* Product Info */}
                <div className="p-4">
                  {/* Type & Duration */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-semibold uppercase">
                      {product.product_type}
                    </span>
                    {product.duration && (
                      <span className="text-xs text-gray-500">
                        {formatDuration(product.duration)}
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="text-base font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-purple-600 transition-colors">
                    {product.title}
                  </h3>

                  {/* Subtitle */}
                  {product.subtitle && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-1">
                      {product.subtitle}
                    </p>
                  )}

                  {/* Seller with verification */}
                  <div className="flex items-center space-x-1 mb-3">
                    <span className="text-xs text-gray-600">by</span>
                    <span className="text-xs font-medium text-gray-900">Certified Seller</span>
                    <CheckCircle className="w-3 h-3 text-purple-600" />
                  </div>

                  {/* Rating & Sales */}
                  <div className="flex items-center justify-between mb-3 text-sm">
                    <div className="flex items-center space-x-1">
                      <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      <span className="font-medium text-gray-900">{product.average_resonance.toFixed(1)}</span>
                      <span className="text-gray-500">({product.total_reflections})</span>
                    </div>
                    <div className="flex items-center space-x-1 text-gray-500">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-xs">{product.total_sales}</span>
                    </div>
                  </div>

                  {/* Mood Tags */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {product.mood_tags?.slice(0, 3).map(tag => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-purple-50 text-purple-600 rounded text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Price & Buy Button */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div>
                      <span className="text-xl font-bold text-gray-900">${product.price.toFixed(2)}</span>
                    </div>
                    <button 
                      onClick={(e) => addToCart(product, e)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium flex items-center space-x-1"
                    >
                      <ShoppingBag className="w-4 h-4" />
                      <span>Add</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600 mb-4">Try adjusting your filters or search query</p>
            <button 
              onClick={() => {
                setSelectedMood('all');
                setSelectedCategory('all');
                setSearchQuery('');
              }}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-12 mt-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Become a Conscious Seller</h2>
          <p className="text-lg text-purple-100 mb-6 max-w-2xl mx-auto">
            Share your transformational content with our community of conscious organizations. 
            Earn revenue while making a positive impact.
          </p>
          <button 
            onClick={goToSellerOnboarding}
            className="px-8 py-3 bg-white text-purple-600 rounded-lg hover:bg-purple-50 transition-colors font-semibold"
          >
            Start Selling Today
          </button>
        </div>
      </div>
    </div>
  );
};

export default Marketplace;